// ─────────────────────────────────────────────────────────────────────────
// `/challenge` — the 30-Day AI-Native Challenge sales page, in waitlist mode.
//
// Dispatched by `MarketingV2App` the same way as `/shop`. Like `/p/<slug>` it
// owns its chrome: a page bought with ad traffic has one job, so there is no
// site nav to leave by.
//
//   /challenge          the page
//   /challenge/         the same page
//   /challenge/for/<a>  the page for one angle (angles.ts); an unknown angle
//                       is the default page
//   ?view=challenge     local dev, from the default surface
//
// `public/challenge/covers/` and `public/challenge/sales/` are static asset
// folders; only the bare path is a page.
// ─────────────────────────────────────────────────────────────────────────

export const CHALLENGE_PATH = '/challenge';

/** Cheap and total: called before the marketing surface renders anything. */
export function isChallengePath(pathname: string, search = ''): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === CHALLENGE_PATH) return true;
  if (/^\/challenge\/for\/[a-z0-9-]{1,40}$/i.test(path)) return true;
  return new URLSearchParams(search).get('view') === 'challenge';
}

/** Absolute URL for the page — for anywhere a full address is wanted. */
export function challengeUrl(origin: string): string {
  return `${origin.replace(/\/$/, '')}${CHALLENGE_PATH}`;
}

/**
 * The first screen's poster is the page's largest paint on a phone, so its
 * fetch starts before the page's own code has loaded: one preload per screen
 * size, matched by media query so each device fetches exactly one image.
 * The page's poster <img> uses the same URLs, so the preload is reused.
 *
 * v10: every look's hero has the same thing under its headline, Tom's video
 * (challenge/Vsl.tsx), so every look preloads its poster. The no-video
 * variant (`?v=novsl`) has no image in its hero (its largest paint is the
 * headline), so nothing is preloaded for it.
 */
export const CHALLENGE_LCP_POSTERS = {
  mobile: '/challenge/sales/vsl-poster-v10-720.webp',
  desktop: '/challenge/sales/vsl-poster-v10-1280.webp',
  /** Phones are anything under 768px, the page's own breakpoint. */
  mobileQuery: '(max-width: 767px)',
  desktopQuery: '(min-width: 768px)',
} as const;

/** The hero posters for an address, or null when its hero has no image. */
export function challengePosters(search = ''): { mobile: string; desktop: string } | null {
  const v = new URLSearchParams(search).get('v')?.trim().toLowerCase();
  return v === 'novsl' ? null : CHALLENGE_LCP_POSTERS;
}

export function preloadChallengePoster(doc: Document = document, search = typeof window !== 'undefined' ? window.location.search : ''): void {
  if (doc.head.querySelector('link[data-challenge-lcp]')) return;
  const posters = challengePosters(search);
  if (!posters) return;
  const add = (href: string, media: string) => {
    const link = doc.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('type', 'image/webp');
    link.setAttribute('href', href);
    link.setAttribute('media', media);
    link.setAttribute('fetchpriority', 'high');
    link.dataset.challengeLcp = '';
    doc.head.appendChild(link);
  };
  add(posters.mobile, CHALLENGE_LCP_POSTERS.mobileQuery);
  add(posters.desktop, CHALLENGE_LCP_POSTERS.desktopQuery);
}
